/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {ModelResponse, nullify, ResponseData, undefine} from '@dvrd/dvr-controls';

import {CountryCode, VATValue} from '../util/interfaces';

import {
    getBolProductByEan,
    getProducts,
    getProductSelection,
    importProductsAsync,
    updateBolProduct
} from '../util/requestSender';

import {ProductSelection} from '../util/types';

import {deepClone} from '../util/utils';

import {CountryVATValues} from '../util/vatUtil';

import Account from './accountModel';

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';

interface Props extends BaseProps {
    ean?: string;
    vats?: Record<string, VATValue> | CountryVATValues;
    account_id?: string;
    title?: string;
    bol_title?: string;
    offer_id?: string;
    is_new?: boolean;
    hidden_by_user?: boolean;
    account?: Account | any;
}

interface Fields extends BaseFields {
    ean: string;
    vats: CountryVATValues;
    account_id: string;
    title: string;
    bol_title: string;
    offer_id: string;
    is_new: boolean;
    hidden_by_user: boolean;
    account: Account | null;
    image_url: string | null;
}

const fields: Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    ean: '',
    vats: new CountryVATValues(),
    account_id: '',
    title: '',
    bol_title: '',
    offer_id: '',
    is_new: false,
    hidden_by_user: false,
    account: null,
    image_url: null
};

const fieldsDefinition: {[key in keyof Omit<Fields, 'account'>]: FieldDefinition} = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    ean: createFieldDef(ModelFieldType.STRING),
    account_id: createFieldDef(ModelFieldType.STRING),
    title: createFieldDef(ModelFieldType.STRING),
    bol_title: createFieldDef(ModelFieldType.STRING),
    offer_id: createFieldDef(ModelFieldType.STRING),
    vats: createFieldDef(ModelFieldType.OBJECT, null, true),
    is_new: createFieldDef(ModelFieldType.BOOLEAN),
    image_url: createFieldDef(ModelFieldType.STRING, null, true),
    hidden_by_user: createFieldDef(ModelFieldType.BOOLEAN)
};

const ignoreProps: Array<keyof Props> = ['vats'];

export default class BolProduct extends BaseModel<Props, Fields> {
    static getAll = (
        params: {
            accountId: string;
            limit: number;
            page: number;
            search: string;
            eans?: Array<string>;
            sortCustomVAT?: boolean;
            withImages?: boolean;
            excludeOnHold?: boolean;
            hiddenProducts?: boolean;
        },
        callback: ModelResponse<Array<BolProduct>>
    ) => {
        getProducts({
            data: {
                account_id: params.accountId,
                limit: params.limit,
                page: params.page,
                search: params.search,
                eans: params.eans,
                sort_custom_vat: params.sortCustomVAT,
                with_images: params.withImages,
                exclude_on_hold: params.excludeOnHold,
                hidden_products: params.hiddenProducts
            },
            callback: (data: ResponseData) => {
                const products: BolProduct[] = data.success
                    ? data.products.map((product: any) => new BolProduct(product))
                    : [];
                callback(products, data.success, data);
            }
        });
    };

    static getSelection = (
        params: {
            accountID: string;
            page: number;
            limit: number;
            search?: string | null;
        } & ProductSelection,
        callback: ModelResponse<Array<BolProduct>>
    ) => {
        return getProductSelection({
            data: {
                account_id: params.accountID,
                invert: params.invert,
                clauses: params.clauses,
                limit: params.limit,
                page: params.page,
                search: undefine(params.search)
            },
            callback: (data: ResponseData) => {
                const products: BolProduct[] = data.success
                    ? data.products.map((product: any) => new BolProduct(product))
                    : [];
                callback(products, data.success, data);
            }
        });
    };

    static importAsync = (account_id: string, useCrawler: boolean, callback: ModelResponse<Account | null>) => {
        // TODO: API contract mismatch — `useCrawler` parameter is silently ignored, `use_crawler: false`
        // is always sent. Either wire the parameter through (`use_crawler: useCrawler`) or remove it
        // from the signature so callers know it has no effect.
        importProductsAsync({
            data: {account_id, use_crawler: false},
            callback: (data: ResponseData) => {
                const account: Account | null = data.success ? new Account(data.account) : null;
                callback(account, data.success, data);
            }
        });
    };

    static getByEAN = (accountID: string, ean: string, callback: ModelResponse<BolProduct | null>) => {
        return getBolProductByEan({
            account_id: accountID,
            ean,
            callback: (data: ResponseData) => {
                const product = data.success ? new BolProduct(data.product) : null;
                callback(product, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get ean(): string {
        return this.fields.ean;
    }

    set ean(ean: string) {
        this.fields.ean = ean;
    }

    get accountId(): string {
        return this.fields.account_id;
    }

    set accountId(account_id: string) {
        this.fields.account_id = account_id;
    }

    get title(): string {
        return this.fields.title;
    }

    get actualTitle(): string {
        return nullify(this.fields.title) ?? this.fields.bol_title;
    }

    set title(title: string) {
        this.fields.title = title;
    }

    get offerId(): string {
        return this.fields.offer_id;
    }

    set offerId(offer_id: string) {
        this.fields.offer_id = offer_id;
    }

    get account(): Account | null {
        return this.fields.account;
    }

    set account(account: Account | null) {
        this.fields.account = account;
        if (account) this.accountId = account.id;
    }

    get vats(): CountryVATValues {
        return this.fields.vats;
    }

    set vats(vats: CountryVATValues | Record<string, VATValue>) {
        if (!(vats instanceof CountryVATValues)) vats = new CountryVATValues(vats);
        this.fields.vats = vats;
    }

    get isNew(): boolean {
        return this.fields.is_new;
    }

    get imageURL(): string | null {
        return this.fields.image_url;
    }

    set imageURL(imageURL: string | null) {
        this.fields.image_url = imageURL;
    }

    get hiddenByUser(): boolean {
        return this.fields.hidden_by_user;
    }

    set hiddenByUser(value: boolean) {
        this.fields.hidden_by_user = value;
    }

    getCountryVat = (countryCode: CountryCode | string): VATValue | null => {
        return this.vats.getCountryValue(countryCode);
    };

    update = (retroactive: boolean, callback: ModelResponse<BolProduct>) => {
        updateBolProduct({
            data: {
                account_id: this.accountId,
                product_id: this.id,
                vats: this.vats.dump(true),
                title: nullify(this.title),
                apply_retroactive: retroactive,
                hidden_by_user: this.hiddenByUser
            },
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.bol_product);
                callback(this, data.success, data);
            }
        });
    };

    clone = (deep: boolean = true): BolProduct => {
        let fields = this.fields;
        if (deep) fields = deepClone(fields);
        return new BolProduct(fields);
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (props.vats) {
                this.fields.vats =
                    props.vats instanceof CountryVATValues ? props.vats : new CountryVATValues(props.vats);
            }
        }
    };
}
